import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { auth } from '@/lib/api';
import { setStoredUser, setStoredToken, initiateGoogleLogin } from '@/lib/auth';
import { VALID_PLAN_IDS, isValidBillingCycle } from '@/components/PricingSection';
import { Clock, Loader2, Mail } from 'lucide-react';
import BrandLogo from '@/components/BrandLogo';

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleChange = (e) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  // The visitor's plan choice from the public pricing page (?plan=<id>), if any.
  // Preserved when navigating to /register so the choice survives in both directions.
  const planParam = searchParams.get('plan');
  const hasValidPlan = VALID_PLAN_IDS.includes(planParam);

  // Annual billing chosen on the public pricing page (?cycle=annual), if any.
  const cycleParam = isValidBillingCycle(searchParams.get('cycle'))
    ? searchParams.get('cycle')
    : null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await auth.login(formData);
      const { token, user } = response.data;

      setStoredToken(token);
      setStoredUser(user);

      toast.success('Welcome back!');
      // If the visitor picked a plan on the public pricing page, land them on
      // the billing page with that plan pre-selected instead of the dashboard.
      if (hasValidPlan && user.role === 'employer') {
        navigate(
          `/employer/billing?plan=${planParam}` +
            (cycleParam === 'annual' ? '&cycle=annual' : '')
        );
        return;
      }
      // Redirect based on role
      if (user.role === 'subscription_manager') {
        navigate('/admin');
      } else if (user.role === 'employer') {
        navigate('/employer');
      } else {
        navigate('/toc');
      }
    } catch (error) {
      if (error.response) {
        // The server answered: this is a genuine auth rejection
        toast.error(error.response.data?.detail || 'Invalid credentials');
      } else {
        // No response at all: network/transport failure, not a credentials problem
        toast.error('Connection error — please try again');
      }
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoogleLogin = () => {
    initiateGoogleLogin('toc', hasValidPlan ? planParam : null, cycleParam);
  };
  
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-2 w-fit">
            <BrandLogo variant="black" size="lg" />
          </Link>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <Card className="w-full max-w-md border-slate-200 rounded-xl shadow-sm">
          <CardHeader className="text-left">
            <CardTitle className="text-2xl font-bold font-['Manrope'] text-slate-900">
              Welcome back
            </CardTitle>
            <CardDescription className="text-slate-600">
              Sign in to your account to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="you@example.com"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  data-testid="login-email-input"
                  className="rounded-md"
                />
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <Link
                    to="/forgot-password"
                    className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
                    data-testid="login-forgot-password-link"
                  >
                    Forgot password?
                  </Link>
                </div>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={isLoading}
                  data-testid="login-password-input"
                  className="rounded-md"
                />
              </div>

              <Button
                type="submit" 
                className="w-full bg-slate-900 hover:bg-slate-800 rounded-md"
                disabled={isLoading}
                data-testid="login-submit-btn"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
            
            <div className="relative my-6">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white px-2 text-xs text-slate-500">
                or continue with
              </span>
            </div>
            
            <Button
              type="button"
              variant="outline"
              className="w-full rounded-md border-slate-300"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              data-testid="login-google-btn"
            >
              <Mail className="mr-2 h-4 w-4" />
              Sign in with Google
            </Button>
            
            <p className="mt-6 text-center text-sm text-slate-600">
              Don't have an account?{' '}
              <Link
                to={
                  hasValidPlan
                    ? `/register?role=employer&plan=${planParam}` +
                      (cycleParam === 'annual' ? '&cycle=annual' : '')
                    : '/register'
                }
                className="font-medium text-emerald-600 hover:text-emerald-700"
                data-testid="login-register-link"
              >
                Create one
              </Link>
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
